import React, { FC, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'wouter';
import dayjs from 'dayjs';
import * as PageQuery from 'graphql/page.graphql';
import { currentSpaceSlug } from 'api/currentSpaceMiddleware';
import { currencyFormatter, showPlural } from 'utils';
import CopyPageModal from 'components/CopyPageModal';
import DropDown from 'components/Menu/DropDown';
import DropDownItem from 'components/Menu/DropDownItem';
import Tag from 'components/Tag';
import { PageCardData } from 'api/data/pages/types';
import { PagesStatus } from 'defaults/page';
import './style.scss';

const MaxDaysForNewTag = 8;

const PageCard: FC<PageCardData> = ({ data }: PageCardData) => {
  const [showModal, setShowModal] = useState(false);
  const [updateStatusMutation] = useMutation(PageQuery.UpdatePageStatus, {
    refetchQueries: [{ query: PageQuery.PagesQuery }],
  });

  const spaceSlug = currentSpaceSlug() || '';

  const modalRef = useRef<HTMLDivElement>(null);

  const archiveOrRestorePage = async () => {
    const status = data.status === 'archived' ? PagesStatus.ACTIVE : PagesStatus.ARCHIVED;
    await updateStatusMutation({ variables: { id: data.id, status } });
  };

  const { slug, status, updatedAt, responsesCount, isTemplate } = data;
  const pageLink = `/${spaceSlug}/pages/${slug}/${status === 'archived' ? 'activity' : 'edit'}`;
  const parsedUpdatedAt = dayjs(updatedAt).fromNow();
  const parsedResponsesCount = `${responsesCount} response${showPlural(responsesCount)}`;
  const parsedCollectedAmount = currencyFormatter(data.totalCollectedCents);
  const needNewTag = !isTemplate && dayjs().diff(dayjs(data.createdAt), 'day') < MaxDaysForNewTag;

  return (
    <div className="page-card-row row align-center">
      <Link className="page-card row direction-column gap-s" href={pageLink}>
        <div>
          {(isTemplate || needNewTag) && (
            <span className="tags-container">
              {isTemplate && <Tag type="neutral" label="Template" />}
              {needNewTag && <Tag type="new" label="New" />}
            </span>
          )}
          <strong>{data.title}</strong>
        </div>

        <div className="page-info row gap-s">
          <div>{parsedUpdatedAt}</div>
          <div>•</div>
          <div data-testid="page-responses">{parsedResponsesCount}</div>
          <div>•</div>
          <div data-testid="page-collected">{parsedCollectedAmount}</div>
        </div>
      </Link>

      <CopyPageModal pageId={data.id} showModal={showModal} setShowModal={setShowModal} />

      <DropDown outsideRef={modalRef}>
        <DropDownItem onClick={() => void setShowModal(true)} icon="copy">
          Copy to...
        </DropDownItem>
        <DropDownItem icon="archive" onClick={() => void archiveOrRestorePage()}>
          {data.status === 'archived' ? 'Restore' : 'Archive'}
        </DropDownItem>
      </DropDown>
    </div>
  );
};

export default PageCard;
